import React, { ChangeEvent, HTMLProps, MouseEvent, useEffect } from 'react';
import Icon from '../../icons/Icon';
import DataType from '../../constant/DataType';
import { VariableProperties } from '../../domain/datatype/VariablePropertiesDomains';
import { createPortal } from 'react-dom';
import LanguageUtil from '../../util/LanguageUtil';

function FormicaSearchSelectBox(props: FormicaSearchSelectBoxProps) {
  const [selectedItem, setSelectedItem] = React.useState<FormicaSelectBoxItem | null>(null);
  const [search, setSearch] = React.useState<string>('');
  const [searchResults, setSearchResults] = React.useState<FormicaSelectBoxItem[]>([]);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const selectBoxRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside); // mount
    return () => document.removeEventListener('click', handleClickOutside); // unmount
  }, []);

  useEffect(() => {
    const item = props.options.find((option: FormicaSelectBoxItem) => option.label == props.value || option.value == props.value);
    if (item) {
      setSelectedItem(item);
      setSearch(item.label);
    } else {
      setSelectedItem(null);
      setSearch('');
    }
  }, [props.options, props.value]);

  useEffect(() => {
    if (search == '' || search.trim() == '') {
      setSearchResults(props.options);
      return;
    }
  }, [search, props.options]);

  useEffect(() => {
    if (isOpen) {
      document.querySelector('.scrollable-editor-container')?.classList.remove('overflow-y-scroll');
      if (!document.querySelector('.scrollable-editor-container')?.classList.contains('overflow-y-hidden')) {
        document.querySelector('.scrollable-editor-container')?.classList.add('overflow-y-hidden');
      }
    } else {
      if (!document.querySelector('.scrollable-editor-container')?.classList.contains('overflow-y-scroll')) {
        document.querySelector('.scrollable-editor-container')?.classList.add('overflow-y-scroll');
      }
      document.querySelector('.scrollable-editor-container')?.classList.remove('overflow-y-hidden');
    }
  }, [isOpen]);

  const handleClickOutside = (event: MouseEvent | any) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (target.closest('.not-dismissable')) {
      return;
    }

    if (selectBoxRef.current && !selectBoxRef.current.contains(target)) {
      setIsOpen(false);
      setSearch('');
    }
  };

  const onChangeValue = (e: ChangeEvent<HTMLInputElement>) => {
    const { disabled } = props;
    if (disabled) return;
    setSelectedItem(null);
    const filteredOptions: FormicaSelectBoxItem[] = [];
    props.options.forEach((option: FormicaSelectBoxItem) => {
      if (option.label.toLowerCase().includes(e.target.value.toLowerCase())) {
        filteredOptions.push(option);
      }
    });
    setSearchResults(filteredOptions);
    setSearch(e.target.value);
  };

  const onClickListItem = (item: FormicaSelectBoxItem) => {
    const { disabled } = props;
    if (disabled) return;

    setSelectedItem(item);
    setSearch(item.label);
    props.onChange && props.onChange(item);
    setSearchResults(props.options);
    setIsOpen(false);
  };

  const onClickClear = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setSelectedItem(null);
    setSearch('');
    props.onChange && props.onChange({ value: '', label: '' });
  };

  const getCustomSize = (customSize: 'x-small' | 'small' | 'medium' | 'large') => {
    switch (customSize) {
      case 'x-small':
        return 'h-[24px] min-h-[24px]';
      case 'small':
        return 'h-[28px] min-h-[28px]';
      case 'medium':
        return 'h-[34px] min-h-[34px]';
      case 'large':
        return 'h-[40px] min-h-[40px]';
      default:
        return 'h-[34px] min-h-[34px]';
    }
  };

  const getClearButton = () => {
    return (
      <div className={'px-0.5 py-0.5 bg-secondary rounded-full opacity-50 flex items-center justify-center hover:opacity-100'} onClick={onClickClear}>
        <Icon icon={'close'} size={18} className={'text-f-black-3 group-focus:text-f-purple'} />
      </div>
    );
  };

  const getSearchResults = () => {
    const { disabled } = props;
    if (disabled) return;
    const topSpace = props.customSize == 'x-small' ? 24 : props.customSize == 'small' ? 28 : props.customSize == 'medium' ? 34 : 40;
    const menuHeight = searchResults.length == 0 ? 55 : searchResults?.length < 6 ? searchResults.length * 44 : 240;
    const top =
      window.screen.height - selectBoxRef?.current?.getBoundingClientRect().top! > 390
        ? selectBoxRef?.current?.getBoundingClientRect().top! + topSpace
        : selectBoxRef?.current?.getBoundingClientRect().top! - menuHeight;
    return (
      <div
        style={{
          zIndex: 99999999,
          top: top + 'px',
          width: selectBoxRef?.current?.getBoundingClientRect().width + 'px',
          left: selectBoxRef?.current?.getBoundingClientRect().left + 'px',
          boxShadow: '0px 4px 6px -2px #00000008,0px 12px 16px -4px #00000014',
          minWidth: '220px',
        }}
        className={`${isOpen ? 'visible' : 'hidden'} flex flex-col bg-white max-h-[240px] overflow-scroll absolute border-[1px] not-dismissable rounded-[4px]`}
      >
        {searchResults && searchResults.length <= 0 ? (
          <div className={'flex items-center justify-center text-f-black-3  w-full h-12 '}>
            <Icon icon={'no-folder'} size={20} />
            {LanguageUtil.get({ id: 'common.label.noResult', defaultMessage: 'No Result Found' })}
          </div>
        ) : (
          searchResults?.map((result: FormicaSelectBoxItem, index: number) => {
            return (
              <div
                key={result.value + index}
                onClick={() => onClickListItem(result)}
                data-testid={`select-item-${result.value}`}
                className={`flex truncate text-sm text-f-black-2 cursor-pointer h-[40px] min-h-[40px] items-center justify-start gap-x-2 px-3 py-2 hover:bg-[#F7F8FA] shrink-0 overflow-hidden`}
              >
                {result.icon && <Icon icon={result.icon || 'search'} size={18} className={'text-f-primary'} />}
                {result.iconNode && result.iconNode}
                {result.label}
              </div>
            );
          })
        )}
      </div>
    );
  };
  const _boxShadow = props.ghost ? '' : '1px 1px 2px 0px #0000000D';

  return (
    <div
      ref={selectBoxRef}
      onClick={(e) => {
        if (props.disabled) return;

        e.stopPropagation();
        setIsOpen(true);
      }}
      style={{
        boxShadow: _boxShadow,
      }}
      className={`flex ${props.disabled ? 'opacity-50 cursor-not-allowed hover:border-f-black-5' : 'cursor-pointer'} ${props.ghost ? 'border-transparent bg-transparent shadow-none' : 'border-f-black-5 shadow-sm shadow-black/2 bg-white  '} text-md select-none focus-within:border-f-purple focus-within:formica-shadow items-center justify-start ${getCustomSize(props.customSize || 'medium')}  ${props.rounded ? 'rounded-[4px]' : 'rounded-[2px]'} relative  border-[1px] w-full group hover:border-f-purple ${props.selectBoxClassName && props.selectBoxClassName}  `}
    >
      <div className={'flex items-center w-full gap-x-2 h-full justify-start px-0.5'} data-testid={(props as any)['data-testid']}>
        {props.icon && (
          <div className={'flex items-center border-r h-full justify-center w-[32px] min-w-[32px]'}>
            <Icon icon={props.icon ? props.icon : 'search'} size={18} className={'text-primary group-focus:text-f-purple text-f-primary '} />
          </div>
        )}
        <div className={'flex h-full items-center gap-x-3 justify-center w-full px-1'}>
          {props.iconNode && props.iconNode}
          <input
            placeholder={props.placeholder}
            value={selectedItem ? selectedItem.label : search}
            disabled={props.disabled}
            onChange={(e) => onChangeValue(e)}
            className={`border-none h-full w-full disabled:bg-white ${props.ghost ? 'bg-transparent text-f-black-3' : 'bg-white text-primary'} outline-none disabled:cursor-not-allowed focus:cursor-text  group-focus-within:text-f-purple group-focus:text-f-purple  text-sm 2xl:text-md  truncate ${props.className}`}
          />
          {props.showClear && selectedItem != null && getClearButton()}
        </div>
        <div className={'flex items-center justify-start w-3 mr-1'}>
          <Icon icon={'arrow-down'} size={20} className={'text-f-black-3 group-focus:text-f-purple'} />
        </div>
      </div>
      {isOpen &&
        createPortal(
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {getSearchResults()}
          </div>,
          document.body,
        )}
    </div>
  );
}

export interface FormicaSearchSelectBoxProps extends Omit<HTMLProps<HTMLInputElement>, 'onChange'> {
  options: FormicaSelectBoxItem[];
  onChange?: (e: FormicaSelectBoxItem) => void;
  icon?: string;
  iconNode?: React.ReactNode;
  rounded?: boolean;
  customSize?: 'x-small' | 'small' | 'medium' | 'large';
  showClear?: boolean;
  ghost?: boolean;
  selectBoxClassName?: string;
  'data-testid'?: string;
}

export interface FormicaSelectBoxItem {
  value: string | any;
  dataType?: DataType;
  properties?: VariableProperties;
  label: string;
  icon?: string;
  iconNode?: React.ReactNode;
}

export default FormicaSearchSelectBox;
